import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Paper from '@mui/material/Paper';
import { TableRow } from '@mui/material';
import CustomButton from './CustomButton';
import SortIcon from '../assets/sort-icon.svg';
import InfoIcon from '../assets/info-icon.svg';
import CustomTooltip from './CustomTooltip';

import './CustomTable.css';

export type TableHeaderData<T> = {
  key: keyof T | any;
  label: string;
  subLabel?: string;
  icons?: Array<'sort' | 'info'>;
  renderCell?: (row: T) => React.ReactNode;
};
interface CustomTableProps<T extends { [key: string]: string }> {
  tableHeaderData: TableHeaderData<T>[];
  size?: 'small' | 'medium';
  tableBodyData?: T[];
  children?: React.ReactNode;
  headerTooltip?: boolean;
  caption?: React.ReactNode;
}

export default function CustomTable<T extends { [key: string]: any }>({
  tableHeaderData,
  size = 'medium',
  tableBodyData,
  children,
  headerTooltip,
  caption,
}: CustomTableProps<T>) {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [sortBy, setSortBy] = useState<keyof T | null>(null);

  const updateSortValues = (key: keyof T) => {
    setSortOrder(sortOrder && sortOrder === 'desc' ? 'asc' : 'desc');
    setSortBy(key);
  };

  const sortData = () => {
    if (!sortBy || !sortOrder) return tableBodyData;

    return tableBodyData?.sort((element1, element2) => {
      let result = element1[sortBy].localeCompare(element2[sortBy]);

      return sortOrder === 'desc' ? result : -result;
    });
  };

  const iconNodes = {
    sort: (label: string, key: keyof T) => (
      <CustomButton
        key={0}
        variant="clear"
        buttonText={label}
        icon={<img src={SortIcon} alt={`sort table by ${label}`}></img>}
        iconPosition="end"
        buttonAction={() => updateSortValues(key)}
        sx={{
          fontWeight: '600',
          padding: 0,
          '& .MuiButton-endIcon': {
            marginRight: 0,
            marginLeft: 'var(--scaling-spacing-4)',
          },
        }}
      />
    ),
    info: () => <img key={1} src={InfoIcon} alt="info"></img>,
  };

  const renderHeaderCellData = (
    withTooltip: boolean,
    icons: ('sort' | 'info')[] | undefined,
    label: string,
    key: string,
    subLabel?: string
  ) => {
    const truncatedLabel = label.length > 15 ? `${label.slice(0, 15)}...` : label;

    const renderCellContent = () => {
      return (
        <div className={icons && icons.length > 1 ? 'multiple-icons-cell' : ''}>
          {icons ? (
            icons.map((iconName) => iconNodes[iconName](label, key))
          ) : subLabel ? (
            <>
              <p className="bolded no-margin">{label}</p>
              <span className="regular">{subLabel}</span>
            </>
          ) : (
            <span>{withTooltip ? truncatedLabel : label}</span>
          )}
        </div>
      );
    };

    return withTooltip && label.length > 15 ? (
      <CustomTooltip title={label} placement="top">
        {renderCellContent()}
      </CustomTooltip>
    ) : (
      renderCellContent()
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table size={size}>
        {caption ? <caption>{caption}</caption> : null}
        <TableHead data-testid="table-head">
          <TableRow>
            {tableHeaderData.map(({ key, label, icons, subLabel }, index) => (
              <TableCell key={index} align="left">
                {renderHeaderCellData(headerTooltip || false, icons, label, key, subLabel)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody data-testid="table-body">
          {sortData()?.map((dataRow, rowIndex) => (
            <TableRow key={dataRow.id}>
              {tableHeaderData.map((headerCell, cellIndex) => (
                <TableCell key={`${rowIndex}-${cellIndex}`}>
                  {headerCell.renderCell ? headerCell.renderCell(dataRow) : dataRow[headerCell.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {children}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
